import React, { useEffect, useState } from 'react'
import { getThumbnailCaptureState, subscribeThumbnailCapture } from '../lib/thumbnailCaptureStore'

/**
 * ThumbnailCaptureIndicator — non-invasive viewport pill for the manual
 * launcher-thumbnail capture (ENG-1079). Gives the click immediate feedback:
 * a spinner while capturing, a green check when saved (auto-dismisses), red on
 * error — instead of a blank wait ending in a modal. pointer-events:none so it
 * never blocks the scene. Reads the thumbnailCaptureStore singleton.
 */
export default function ThumbnailCaptureIndicator() {
  const [state, setState] = useState(getThumbnailCaptureState())
  useEffect(() => subscribeThumbnailCapture(setState), [])

  const { phase, message } = state
  if (phase === 'idle') return null

  // --m-* tokens (palette-enforced): danger/online/fg-2 so the pill follows a
  // palette switch instead of pinning literal colors.
  const tone = phase === 'error'
    ? { fg: 'var(--m-danger)', edge: 'color-mix(in srgb, var(--m-danger) 45%, transparent)' }
    : phase === 'saved'
      ? { fg: 'var(--m-online)', edge: 'color-mix(in srgb, var(--m-online) 45%, transparent)' }
      : { fg: 'var(--m-fg-2)', edge: 'var(--m-line)' }

  return (
    <div
      data-testid="thumbnail-capture-indicator"
      data-phase={phase}
      style={{
        position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
        zIndex: 30, display: 'flex', alignItems: 'center', gap: 8,
        background: 'rgba(20,20,20,0.88)', border: `1px solid ${tone.edge}`,
        borderRadius: 6, padding: '5px 12px',
        color: tone.fg, fontFamily: 'var(--m-font)', fontSize: 11.5,
        pointerEvents: 'none',
        animation: 'aos-thumb-cap-in 220ms ease-out',
      }}
    >
      <style>{`
        @keyframes aos-thumb-cap-in { from { opacity: 0; transform: translate(-50%, -4px); } to { opacity: 1; transform: translate(-50%, 0); } }
        @keyframes aos-thumb-cap-spin { to { transform: rotate(360deg); } }
      `}</style>
      {phase === 'capturing' && (
        <span style={{
          width: 11, height: 11, borderRadius: '50%', flexShrink: 0,
          border: '1.5px solid color-mix(in srgb, var(--m-fg-2) 30%, transparent)',
          borderTopColor: 'var(--m-fg-2)',
          animation: 'aos-thumb-cap-spin 0.75s linear infinite',
        }} />
      )}
      {phase === 'saved' && <span style={{ fontSize: 12 }}>✓</span>}
      {phase === 'error' && <span style={{ fontSize: 12 }}>⚠</span>}
      <span>{message}</span>
    </div>
  )
}
